import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import toast from "react-hot-toast";

const {
  FiCpu,
  FiZap,
  FiActivity,
  FiShield,
  FiSend,
  FiRefreshCw,
  FiAlertTriangle,
  FiLock,
  FiCheckCircle,
  FiPlay,
} = FiIcons;

const SIMULATION_PATIENTS = [
  { id: "1", name: "Sarah Johnson" },
  { id: "2", name: "Michael Chen" },
  { id: "3", name: "Emma Davis" },
];

const getStoredTargetPatientId = (user) =>
  localStorage.getItem("simulatorPatientId") ||
  (user?.role === "patient" ? String(user.id) : "");

const DeviceSimulator = () => {
  const { user } = useAuth();
  const [isSimulating, setIsSimulating] = useState(false);
  const [activeScenario, setActiveScenario] = useState("baseline");
  const [targetPatientId, setTargetPatientId] = useState(() =>
    getStoredTargetPatientId(user),
  );
  const [vitals, setVitals] = useState({
    hr: 72,
    bpSys: 120,
    bpDia: 80,
    stress: 0.2,
    sleep: 0.8,
  });
  const [lastResponse, setLastResponse] = useState(null);

  useEffect(() => {
    setTargetPatientId(getStoredTargetPatientId(user));
  }, [user?.id, user?.role]);

  useEffect(() => {
    const handleTargetChange = (event) => {
      if (event.key && event.key !== "simulatorPatientId") return;
      setTargetPatientId(getStoredTargetPatientId(user));
    };

    window.addEventListener("storage", handleTargetChange);
    window.addEventListener("simulatorPatientChanged", handleTargetChange);

    return () => {
      window.removeEventListener("storage", handleTargetChange);
      window.removeEventListener("simulatorPatientChanged", handleTargetChange);
    };
  }, [user]);

  const selectTargetPatient = (patientId) => {
    localStorage.setItem("simulatorPatientId", patientId);
    setTargetPatientId(patientId);
    window.dispatchEvent(
      new CustomEvent("simulatorPatientChanged", {
        detail: { patientId },
      }),
    );
  };

  const scenarios = [
    {
      id: "baseline",
      label: "Stable Baseline",
      icon: FiCheckCircle,
      color: "text-green-500",
      description: "Normal resting vitals — no anomalies expected.",
      data: { hr: 72, bpSys: 120, bpDia: 80, stress: 0.2, sleep: 0.85 },
    },
    {
      id: "tachycardia",
      label: "Cardiac Event (Tachy)",
      icon: FiActivity,
      color: "text-orange-500",
      description: "Elevated heart rate and BP. Tests cardiac alert threshold.",
      data: { hr: 145, bpSys: 155, bpDia: 95, stress: 0.7, sleep: 0.6 },
    },
    {
      id: "crisis",
      label: "Protocol 4422 Trigger",
      icon: FiAlertTriangle,
      color: "text-red-500",
      description: "Critical multi-system breach. Triggers emergency overlay.",
      data: { hr: 185, bpSys: 190, bpDia: 110, stress: 0.95, sleep: 0.3 },
    },
    {
      id: "neuro",
      label: "Neuro-Stability Breach",
      icon: FiZap,
      color: "text-purple-500",
      description: "High stress and poor sleep with elevated neural markers.",
      data: { hr: 105, bpSys: 135, bpDia: 88, stress: 0.9, sleep: 0.2 },
    },
  ];

  const applyScenario = (id) => {
    const scenario = scenarios.find((s) => s.id === id);
    setActiveScenario(id);
    setVitals(scenario.data);
    toast.success(`Applied: ${scenario.label}`);
  };

  const transmitData = async () => {
    setIsSimulating(true);
    try {
      const activeTargetPatientId = getStoredTargetPatientId(user);
      // Prevent sending invalid numeric IDs to the ingestion service
      const uuidV4Regex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (activeTargetPatientId && !uuidV4Regex.test(activeTargetPatientId)) {
        throw new Error(
          `Simulator target id is not a UUID: "${activeTargetPatientId}". Use a valid user UUID or set the simulator target in the UI.`,
        );
      }
      setTargetPatientId(activeTargetPatientId);

      const payload = {
        patientId: activeTargetPatientId,
        provider: "simulator",
        source: "simulator",
        vitals: {
          hr: vitals.hr,
          bp: `${vitals.bpSys}/${vitals.bpDia}`,
          stressMarkers: vitals.stress,
          sleepQuality: vitals.sleep,
        },
        userData: {
          sleepQuality: vitals.sleep,
          currentMood: 1 - vitals.stress,
        },
        thresholds: user?.thresholds || {
          heartRate: { max: 100 },
          bloodPressure: { sys: 140 },
        },
      };

      const response = await api.post("/ingest/vitals", payload);
      setLastResponse(response.data);
      localStorage.setItem(
        `latestVitals:${activeTargetPatientId}`,
        JSON.stringify({
          vitals: payload.vitals,
          receivedAt: response.data?.receivedAt || new Date().toISOString(),
          source: "simulator",
        }),
      );
      window.dispatchEvent(
        new CustomEvent("latestVitalsChanged", {
          detail: { patientId: activeTargetPatientId },
        }),
      );

      if (vitals.hr > 140 || vitals.bpSys > 180) {
        toast.error("CRITICAL ANOMALY INJECTED", {
          icon: "🚨",
          style: { background: "#991b1b", color: "#fff" },
        });
      } else {
        toast.success("Vitals Synchronized");
      }
    } catch (err) {
      // Surface the actual server error to make future debugging easier
      const detail =
        err?.response?.data?.message || err?.response?.status || err.message;
      toast.error(`Transmission Failed — ${detail}`);
      const errorMessage =
        err.response?.data?.message || err.response?.statusText || err.message;
      console.error(
        "[Simulator] transmit error:",
        errorMessage,
        err.response?.data,
      );
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] p-8 lg:p-20">
      <div className="max-w-6xl mx-auto">
        <header className="mb-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="flex items-center space-x-3 text-gray-400 mb-2">
              <SafeIcon icon={FiCpu} className="text-xl" />
              <span className="text-xs font-black uppercase tracking-[0.3em]">
                iVivaCore Gold Master
              </span>
            </div>
            <h1 className="text-5xl font-black text-gray-900 tracking-tighter">
              Device Simulator
            </h1>
            <p className="text-gray-500 mt-2 font-medium">
              Verify end-to-end AI triggers and Protocol 4422 reliability.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center space-x-2 bg-white p-2 rounded-2xl border border-gray-200 shadow-sm">
              {SIMULATION_PATIENTS.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  onClick={() => selectTargetPatient(patient.id)}
                  className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
                    String(targetPatientId) === patient.id
                      ? "bg-gray-900 text-white"
                      : "bg-gray-50 text-gray-500 hover:text-gray-900"
                  }`}
                  title={patient.name}
                >
                  P{patient.id}
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-4 bg-gray-100 p-2 rounded-3xl border border-gray-200">
              <div className="px-6 py-3 bg-white rounded-2xl shadow-sm flex items-center space-x-3">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-black text-gray-900 uppercase tracking-widest">
                  Pipeline Active
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left: Scenarios */}
          <div className="space-y-6">
            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6">
              Simulation Presets
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {scenarios.map((s) => (
                <button
                  key={s.id}
                  onClick={() => applyScenario(s.id)}
                  className={`p-6 rounded-[2rem] border-2 transition-all text-left flex items-center justify-between group ${
                    activeScenario === s.id
                      ? "bg-gray-900 border-gray-900 text-white shadow-2xl shadow-gray-200"
                      : "bg-white border-gray-100 hover:border-gray-200 text-gray-600"
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        activeScenario === s.id ? "bg-white/10" : "bg-gray-50"
                      }`}
                    >
                      <SafeIcon
                        icon={s.icon}
                        className={`text-xl ${activeScenario === s.id ? "text-white" : s.color}`}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-sm">{s.label}</p>
                      <p
                        className={`text-[10px] uppercase font-black tracking-widest ${
                          activeScenario === s.id
                            ? "text-gray-400"
                            : "text-gray-300"
                        }`}
                      >
                        Clinical Spec v2.1
                      </p>
                      {/* Added: preset description for clarity */}
                      <p
                        className={`text-[10px] mt-0.5 leading-snug ${
                          activeScenario === s.id
                            ? "text-gray-400"
                            : "text-gray-400"
                        }`}
                      >
                        {s.description}
                      </p>
                    </div>
                  </div>
                  <SafeIcon
                    icon={FiPlay}
                    className="text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                </button>
              ))}
            </div>

            <div className="bg-cyan-50 p-8 rounded-[2rem] border border-cyan-100">
              <SafeIcon
                icon={FiShield}
                className="text-cyan-600 mb-4 text-2xl"
              />
              <h4 className="font-bold text-gray-900 text-sm mb-2">
                Safety Verification
              </h4>
              <p className="text-xs text-cyan-700 leading-relaxed font-medium">
                Simulating a critical breach will trigger the{" "}
                <strong>Emergency Active Overlay</strong> on all connected
                patient dashboards.
              </p>
            </div>
          </div>

          {/* Right: Manual Controls */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center justify-between mb-12">
                <h3 className="text-xl font-bold text-gray-900">
                  Manual Signal Injection
                </h3>
                <button
                  onClick={() => applyScenario("baseline")}
                  className="p-3 bg-gray-50 text-gray-400 rounded-xl hover:text-gray-900 transition-colors"
                  title="Reset to baseline"
                >
                  <SafeIcon icon={FiRefreshCw} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Heart Rate (BPM)
                    </label>
                    <span className="text-sm font-bold text-gray-900">
                      {vitals.hr}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="40"
                    max="220"
                    value={vitals.hr}
                    onChange={(e) =>
                      setVitals({ ...vitals, hr: parseInt(e.target.value) })
                    }
                    className="w-full accent-red-500 h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Systolic BP
                    </label>
                    <span className="text-sm font-bold text-gray-900">
                      {vitals.bpSys}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="80"
                    max="220"
                    value={vitals.bpSys}
                    onChange={(e) =>
                      setVitals({ ...vitals, bpSys: parseInt(e.target.value) })
                    }
                    className="w-full accent-cyan-500 h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Stress Index (SHAP)
                    </label>
                    <span className="text-sm font-bold text-gray-900">
                      {Math.round(vitals.stress * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={vitals.stress * 100}
                    onChange={(e) =>
                      setVitals({
                        ...vitals,
                        stress: parseInt(e.target.value) / 100,
                      })
                    }
                    className="w-full accent-purple-500 h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Sleep Quality
                    </label>
                    <span className="text-sm font-bold text-gray-900">
                      {Math.round(vitals.sleep * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={vitals.sleep * 100}
                    onChange={(e) =>
                      setVitals({
                        ...vitals,
                        sleep: parseInt(e.target.value) / 100,
                      })
                    }
                    className="w-full accent-blue-500 h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <div className="mt-16 pt-10 border-t border-gray-50 flex flex-col md:flex-row items-center gap-6">
                <button
                  onClick={transmitData}
                  disabled={isSimulating}
                  className="w-full md:w-auto px-12 py-5 bg-gray-900 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest shadow-2xl shadow-gray-200 hover:shadow-gray-300 transition-all active:scale-95 flex items-center justify-center space-x-3 disabled:opacity-50"
                >
                  {isSimulating ? (
                    <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                  ) : (
                    <SafeIcon icon={FiSend} />
                  )}
                  <span>
                    {isSimulating ? "Processing..." : "Transmit Signal"}
                  </span>
                </button>
                <div className="flex items-center space-x-2 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                  <SafeIcon icon={FiLock} className="text-green-500" />
                  <span>AES-256 Encrypted Ingestion</span>
                </div>
              </div>
            </div>

            {/* Pipeline Response */}
            <AnimatePresence>
              {lastResponse && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-900 p-8 rounded-[2.5rem] text-white overflow-hidden relative"
                >
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-xs font-black uppercase tracking-widest opacity-60">
                        Engine Response Payload
                      </h4>
                      <span className="text-[10px] bg-white/10 px-2 py-1 rounded-full font-mono">
                        200 OK
                      </span>
                    </div>
                    <pre className="text-[10px] font-mono text-cyan-400 leading-relaxed overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(lastResponse, null, 2)}
                    </pre>
                  </div>
                  <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceSimulator;
